/**
 * LifecycleActionButton Component
 *
 * Renders a single lifecycle action button with optional confirmation popover.
 * Used by LifecycleActions container to render applicable actions for a record.
 *
 * For 'activate' actions on bounded records (with effectiveTo date), this component
 * will first call the preview API to show a SplicingPreviewModal before executing.
 */

import { useState } from 'react'
import { Button, App, Popconfirm, Tooltip } from 'antd'
import * as Icons from '@ant-design/icons'
import { apiService } from '@/services/apiService'
import type { LifecycleAction, FieldMetadata } from '@/services/metadataService'
import { SplicingPreviewModal } from '@/pages/modules/components/SplicingPreviewModal'
import type {
  SplicingPreviewData,
  SplicingText,
  ActivateRequest,
} from '@/pages/modules/data/types'

/**
 * Props for LifecycleActionButton
 */
export interface LifecycleActionButtonProps {
  /** The action key (e.g., 'activate', 'expire', 'modify') */
  actionKey: string
  /** Action metadata from backend */
  action: LifecycleAction
  /** The record to perform action on */
  record: Record<string, unknown>
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Field metadata for diff display in splicing preview (optional - passed from MetadataTable) */
  fieldMetadata?: Record<string, FieldMetadata>
  /** Key fields from comparison config (for diff display in splicing preview) */
  comparisonKeyFields?: string[]
  /** Field to use for timeline segment labels (from comparison.timeline_label_field), or null to disable labels */
  timelineLabelField?: string | null
  /**
   * Whether to prefix "NEW" to the focus segment label in the timeline.
   * - true: Label becomes "NEW" (if no label) or "NEW - {label}"
   * - false/undefined: Use regular label logic
   */
  showNewPrefix?: boolean
  /** Fields to use for friendly record titles (from display.title_fields) */
  titleFields?: string[]
  /** Called after successful action */
  onSuccess?: () => void
  /** Called for actions that open an editor (e.g., modify) */
  onOpenEditor?: (record: Record<string, unknown>, actionKey: string) => void
}

/**
 * Interpolate placeholders in message templates
 *
 * Supported placeholders:
 * - {entity} - Entity label (e.g., "Offering")
 * - {name} - Record name field if present
 * - {uid} - Record UID
 */
function interpolateMessage(
  template: string,
  entityLabel: string,
  record: Record<string, unknown>
): string {
  return template
    .replace(/\{entity\}/g, entityLabel)
    .replace(/\{name\}/g, String(record.name || record.uid || ''))
    .replace(/\{uid\}/g, String(record.uid || ''))
}

/**
 * Get Ant Design icon component by name
 *
 * Icons are referenced by their export name from @ant-design/icons
 * e.g., 'CheckCircleOutlined', 'CloseCircleOutlined'
 */
function getIconComponent(iconName?: string): React.ReactNode {
  if (!iconName) return null

  // Type assertion needed because Icons is typed as module
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (Icons as any)[iconName] as React.ComponentType | undefined
  if (!IconComponent) {
    console.warn(`[LifecycleActionButton] Unknown icon: ${iconName}`)
    return null
  }

  return <IconComponent />
}

/**
 * LifecycleActionButton Component
 *
 * Renders a button for a single lifecycle action with:
 * - Icon from Ant Design icons
 * - Optional confirmation popover
 * - API call on confirm
 * - Success/error message display
 */
export function LifecycleActionButton({
  actionKey,
  action,
  record,
  endpoint,
  entityLabel,
  fieldMetadata,
  comparisonKeyFields,
  timelineLabelField,
  showNewPrefix,
  titleFields,
  onSuccess,
  onOpenEditor,
}: LifecycleActionButtonProps) {
  const { modal, message } = App.useApp()
  const [loading, setLoading] = useState(false)

  // Tooltip visibility state - controlled to ensure it closes properly on actions
  const [tooltipOpen, setTooltipOpen] = useState(false)

  // Splicing preview state
  const [showSplicingPreview, setShowSplicingPreview] = useState(false)
  const [splicingPreviewData, setSplicingPreviewData] = useState<SplicingPreviewData | null>(null)
  const [splicingText, setSplicingText] = useState<SplicingText | null>(null)

  // Handle actions that open an editor (like modify)
  const handleOpenEditor = () => {
    setTooltipOpen(false) // Close tooltip immediately
    if (onOpenEditor) {
      onOpenEditor(record, actionKey)
    }
  }

  // Close tooltip before starting any action
  const closeTooltipAndExecute = (fn: () => void | Promise<void>) => {
    setTooltipOpen(false) // Close tooltip immediately
    fn()
  }

  /**
   * Fetch splicing preview for activate action
   * Returns true if preview was shown, false if we should proceed directly
   *
   * Preview is shown when there are conflicting records that will be superseded,
   * regardless of whether the draft being activated is bounded or open-ended.
   *
   * Note: fieldMetadata is passed from MetadataTable through SlotActions - no separate fetch needed.
   */
  const fetchSplicingPreview = async (): Promise<boolean> => {
    const uid = record.uid as string
    if (!uid) {
      message.error('Record has no UID')
      return false
    }

    setLoading(true)
    try {
      const request: ActivateRequest = {
        preview: true,
        createContinuations: true,
      }

      const previewResponse = await apiService.post<SplicingPreviewData>(
        `${endpoint}/${uid}/${actionKey}`,
        request
      )

      if (previewResponse.success && previewResponse.data) {
        // Check if this is a previewable response (has supersessions or continuations)
        const data = previewResponse.data
        const meta = previewResponse.meta as { splicingText?: SplicingText; splicingPreviewable?: boolean } | undefined

        // Only show preview if there are timeline impacts
        if (
          meta?.splicingPreviewable &&
          (data.supersessions?.length > 0 || data.continuations?.length > 0)
        ) {
          setSplicingPreviewData(data)
          setSplicingText(meta.splicingText || null)
          setShowSplicingPreview(true)
          return true
        }
      }

      // No preview needed or preview not available - proceed directly
      return false
    } catch (error) {
      console.error(`[LifecycleActionButton] Preview fetch error:`, error)
      // On error, fall back to direct activation
      return false
    } finally {
      setLoading(false)
    }
  }

  /**
   * Execute the activate action with the given options
   */
  const executeActivate = async (createContinuations: boolean) => {
    const uid = record.uid as string
    if (!uid) {
      message.error('Record has no UID')
      return
    }

    setLoading(true)
    try {
      const request: ActivateRequest = {
        preview: false,
        createContinuations,
      }

      const response = await apiService.post(`${endpoint}/${uid}/${actionKey}`, request)

      if (response.success) {
        // Show success message with interpolation
        const successMsg = action.successMessage
          ? interpolateMessage(action.successMessage, entityLabel, record)
          : `${entityLabel} ${actionKey}d successfully`
        message.success(successMsg)
        onSuccess?.()
      } else {
        // Error already shown by apiService
        console.error(`[LifecycleActionButton] ${actionKey} failed:`, response.errors)
      }
    } catch (error) {
      console.error(`[LifecycleActionButton] ${actionKey} error:`, error)
      message.error(`Failed to ${actionKey} ${entityLabel.toLowerCase()}`)
    } finally {
      setLoading(false)
      setShowSplicingPreview(false)
      setSplicingPreviewData(null)
      setSplicingText(null)
    }
  }

  /**
   * Handle splicing preview confirmation
   */
  const handleSplicingConfirm = (createContinuations: boolean) => {
    executeActivate(createContinuations)
  }

  /**
   * Handle splicing preview cancellation
   */
  const handleSplicingCancel = () => {
    setShowSplicingPreview(false)
    setSplicingPreviewData(null)
    setSplicingText(null)
  }

  /**
   * Execute the action directly (non-activate actions or after confirmation)
   */
  const executeAction = async () => {
    const uid = record.uid as string
    if (!uid) {
      message.error('Record has no UID')
      return
    }

    setLoading(true)
    try {
      const response = await apiService.post(`${endpoint}/${uid}/${actionKey}`, {})

      if (response.success) {
        // Show success message with interpolation
        const successMsg = action.successMessage
          ? interpolateMessage(action.successMessage, entityLabel, record)
          : `${entityLabel} ${actionKey}d successfully`
        message.success(successMsg)
        onSuccess?.()
      } else {
        // Error already shown by apiService
        console.error(`[LifecycleActionButton] ${actionKey} failed:`, response.errors)
      }
    } catch (error) {
      console.error(`[LifecycleActionButton] ${actionKey} error:`, error)
      message.error(`Failed to ${actionKey} ${entityLabel.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Show simple confirmation dialog for activate action when no splicing preview is needed
   */
  const showSimpleActivateConfirm = () => {
    const recordName = record.name || record.uid || 'this record'
    modal.confirm({
      title: `Activate ${entityLabel}`,
      content: `Are you sure you want to activate "${recordName}"? This action cannot be undone.`,
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: () => executeActivate(false), // No continuations since no conflicts
    })
  }

  // Handle API call for lifecycle action
  const handleAction = async () => {
    const uid = record.uid as string
    if (!uid) {
      message.error('Record has no UID')
      return
    }

    // For activate action, try to show splicing preview first
    // Preview shows when there are conflicting records to supersede
    if (actionKey === 'activate') {
      const showedPreview = await fetchSplicingPreview()
      if (showedPreview) {
        // Preview modal is now showing, wait for user confirmation
        return
      }
      // No conflicts or preview not available - show simple confirmation dialog
      showSimpleActivateConfirm()
      return
    }

    // For non-activate actions, execute directly
    await executeAction()
  }

  // Render the splicing preview modal (always rendered, visibility controlled by state)
  // Pass historyEndpoint and keyComboHash to enable "Show all versions" toggle
  const splicingModal = (
    <SplicingPreviewModal
      visible={showSplicingPreview}
      previewData={splicingPreviewData}
      splicingText={splicingText}
      loading={loading}
      fieldMetadata={fieldMetadata ?? undefined}
      keyFields={comparisonKeyFields}
      timelineLabelField={timelineLabelField}
      showNewPrefix={showNewPrefix}
      entityLabel={entityLabel}
      titleFields={titleFields}
      historyEndpoint={endpoint.replace(/^\//, '')}
      keyComboHash={record.keyComboHash as string | undefined}
      onConfirm={handleSplicingConfirm}
      onCancel={handleSplicingCancel}
    />
  )

  // Common tooltip props - controlled visibility with fast hide
  const tooltipProps = {
    title: action.label,
    destroyOnHidden: true,
    open: tooltipOpen && !loading, // Hide when loading
    onOpenChange: setTooltipOpen,
    mouseLeaveDelay: 0, // Hide immediately on mouse leave
  }

  // For actions that open an editor, don't show popconfirm
  if (action.opensEditor) {
    return (
      <>
        <Tooltip {...tooltipProps}>
          <Button
            type={action.buttonType || 'default'}
            danger={action.danger}
            icon={getIconComponent(action.icon)}
            onClick={handleOpenEditor}
            size="small"
          />
        </Tooltip>
        {splicingModal}
      </>
    )
  }

  // For activate actions, skip the Popconfirm - the splicing preview modal handles confirmation
  // This way we show the fancy preview UI instead of a simple "are you sure?" dialog
  if (actionKey === 'activate') {
    return (
      <>
        <Tooltip {...tooltipProps}>
          <Button
            type={action.buttonType || 'default'}
            danger={action.danger}
            icon={getIconComponent(action.icon)}
            onClick={() => closeTooltipAndExecute(handleAction)}
            loading={loading}
            size="small"
          />
        </Tooltip>
        {splicingModal}
      </>
    )
  }

  // For actions with confirmation message, wrap in Popconfirm
  if (action.confirmationMessage) {
    const confirmMsg = interpolateMessage(action.confirmationMessage, entityLabel, record)

    return (
      <>
        <Tooltip {...tooltipProps}>
          <Popconfirm
            title={confirmMsg}
            onConfirm={() => closeTooltipAndExecute(handleAction)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: action.danger }}
          >
            <Button
              type={action.buttonType || 'default'}
              danger={action.danger}
              icon={getIconComponent(action.icon)}
              loading={loading}
              size="small"
            />
          </Popconfirm>
        </Tooltip>
        {splicingModal}
      </>
    )
  }

  // No confirmation - direct action
  return (
    <>
      <Tooltip {...tooltipProps}>
        <Button
          type={action.buttonType || 'default'}
          danger={action.danger}
          icon={getIconComponent(action.icon)}
          onClick={() => closeTooltipAndExecute(handleAction)}
          loading={loading}
          size="small"
        />
      </Tooltip>
      {splicingModal}
    </>
  )
}
